import React, { useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import productsList from "../../../data/products_list_success.json";
import { CircularProgress, Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import config from "../../../config";

const ProductsColumns = [
  {
    id: "store_name",
    label: "Store Name",
    render: (row) => row.store_name ?? "N/A"
  },
  {
    id: "product_id",
    label: "Product ID",
    noWrap: false,
    render: (row) => row.product_id ?? "N/A"
  },
  {
    id: "product_name",
    label: "Product Name",
    noWrap: false,
    render: (row) => row.product_name ?? "N/A"
  },
  {
    id: "product_price",
    label: "Product Price",
    render: (row) =>
      row.product_price != null
        ? `$ ${Number(row.product_price).toLocaleString()}`
        : "N/A"
  },
  {
    id: "category",
    label: "Category",
    render: (row) => row.category ?? "N/A"
  },
  {
    id: "in_stock",
    label: "In Stock",
    render: (row) => row.in_stock ?? "N/A"
  },
  {
    id: "boost_sales",
    label: "Boost Sales",
    render: (row) => row.boost_sales ?? "N/A"
  },
  {
    id: "last_sync_date",
    label: "Last Sync Date",
    render: (row) => row.last_sync_date ?? "N/A"
  }
];



function ProductsList() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [boostingProductId, setBoostingProductId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [lastSyncTime, setLastSyncTime] = useState(null); // ✅ Store last sync timestamp
  const navigate = useNavigate();

  // Fetch products on component mount
  useEffect(() => {
    let abort = false;

    (async () => {
      try {
        setLoading(true);
        const clientId = config.clientId;

        if (!clientId) {
          console.error("Client ID not found");
          if (!abort) setRows([]);
          return;
        }

        // Fetch products for client
        const url = `${config.API_BASE_URL}/client/product/products?client_id=${clientId}&limit=5000&offset=0`;

        const res = await fetch(url, { headers: config.getHeaders() });
        const json = await res.json();

        if (!res.ok || json?.success === false) {
          if (!abort) setRows([]);
          return;
        }

        // Map backend response to frontend format
        const data = Array.isArray(json.data?.products) ? json.data.products : [];
        const storesMap = {};
        if (json.data?.stores_info) {
          json.data.stores_info.forEach(store => {
            storesMap[store.client_store_id] = store.store_name;
          });
        }

        const normalized = data.map((p) => {
          let lastSyncDate = "-";
          if (p.modified_date) {
            const d = new Date(p.modified_date);
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const year = d.getFullYear();
            lastSyncDate = `${month}-${day}-${year}`;
          }
          return {
            product_id: p.product_id,
            store_name: storesMap[p.client_store_id] || "N/A",
            // product_code: p.product_sku || p.product_id,
            product_name: p.product_name || "N/A",
            product_price: p.product_price || 0,
            category: p.product_type || "N/A",
            in_stock: p.inventory_quantity > 0,
            boost_sales: p.is_boost ? "Yes" : "No",
            is_boost: p.is_boost,
            last_sync_date: lastSyncDate
          };
        });

        if (!abort) setRows(normalized);

        // ✅ Fetch last sync time for products
        try {
          const batchUrl = `${config.API_BASE_URL}/batch/recent?client_id=${clientId}&limit=1`;
          const batchRes = await fetch(batchUrl, { headers: config.getHeaders() });
          const batchJson = await batchRes.json();

          if (batchRes.ok && batchJson.success && batchJson.data && batchJson.data.length > 0) {
            const lastBatch = batchJson.data[0];
            // Check if it's a product or both sync
            if (lastBatch.batch_type === 'product' || lastBatch.batch_type === 'both') {
              if (lastBatch.finished_at) {
                setLastSyncTime(lastBatch.finished_at);
                console.log("📅 Last product sync time loaded:", lastBatch.finished_at);
              }
            }
          }
        } catch (batchErr) {
          console.warn("⚠️ Could not fetch last sync time:", batchErr);
        }

      } catch (_e) {
        console.error("Error fetching products:", _e);
        if (!abort) setRows([]);
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, []);

  // Handle boost/deboost action
  const handleBoostAction = async (row, newBoostStatus) => {
    try {
      setBoostingProductId(row.product_id);
      const clientId = config.clientId;

      const response = await fetch(`${config.API_BASE_URL}/client/product/products/boost`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          product_id: row.product_id,
          client_id: clientId,
          is_boost: newBoostStatus
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error("Failed to update boost status:", result);
        return;
      }

      console.log(`✅ Product boost status updated: ${row.product_name} - is_boost: ${newBoostStatus}`);

      // Refresh the page to fetch updated data from backend
      window.location.reload();
    } catch (error) {
      console.error("Error updating boost status:", error);
    } finally {
      setBoostingProductId(null);
    }
  };

  // 🔥 Custom Action: Boost Sales
  const customActions = [
    {
      label: "Boost Sales",
      icon: "rocket",
      color: "success",
      onClick: (row) => handleBoostAction(row, true),
      disabled: (row) => row.is_boost === true || boostingProductId === row.product_id
    },
    {
      label: "De-Boost Sales",
      icon: "arrow_cool_down",
      color: "error",
      onClick: (row) => handleBoostAction(row, false),
      disabled: (row) => row.is_boost === false || boostingProductId === row.product_id
    }
  ];

  // Handle product sync for all stores
  const handleSyncProducts = async () => {
    try {
      setSyncing(true);
      const clientId = config.clientId;

      if (!clientId) {
        setSnackbar({
          open: true,
          message: "Client ID not found",
          severity: "error"
        });
        return;
      }

      console.log("🔄 Starting product sync for all stores...");

      // Call batch sync API with query parameters
      const url = new URL(`${config.API_BASE_URL}/batch/sync/client`);
      url.searchParams.append('client_id', clientId);
      url.searchParams.append('batch_type', 'product');

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: config.getHeaders()
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to sync products");
      }

      console.log("✅ Product sync started:", result);

      // ✅ Store last sync timestamp from response (from details array)
      if (result.details && result.details.length > 0 && result.details[0].finished_at) {
        setLastSyncTime(result.details[0].finished_at);
        console.log("📅 Last sync time updated:", result.details[0].finished_at);

        setSnackbar({
          open: true,
          message: "Products synced successfully!",
          severity: "success"
        });

        // ✅ Refresh notifications immediately after successful sync
        console.log("🔔 Dispatching notification refresh event...");
        window.dispatchEvent(new CustomEvent('refreshNotifications'));
      } else {
        console.warn("⚠️ No finished_at in response. result.details:", result.details);
        setSnackbar({
          open: true,
          message: "Sync failed to complete",
          severity: "warning"
        });
      }

    } catch (error) {
      console.error("Product sync error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to start product sync",
        severity: "error"
      });
    } finally {
      setSyncing(false);
    }
  };

  // ✅ Format last sync time for display as MM-DD-YYYY HH:MM:SS
  const formatLastSync = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  };

  // 🔥 Custom Header Button: Sync Products with Last Sync Time
  const getSyncButtonLabel = () => {
    if (syncing) return "Syncing...";
    if (lastSyncTime) {
      const formatted = formatLastSync(lastSyncTime);
      return `Sync Products (Last: ${formatted})`;
    }
    return "Sync Products";
  };

  const customHeaderButtons = [
    {
      label: getSyncButtonLabel(),
      icon: "sync",
      onClick: handleSyncProducts,
      disabled: syncing
    }
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <GenericTable
        title="Products List"
        columns={ProductsColumns}
        rows={rows}
        showView={true}
        showEdit={false}
        showDelete={false}
        dropdownFilters={["store_name"]}
        rangeFilters={["product_price"]}
        customActions={customActions}
        customActionsHeader="Sales"
        customHeaderButtons={customHeaderButtons}
        onView={(row) => navigate(`/products/product-details/${row.product_id}`)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ProductsList;
